'use strict';

import React from 'react';
import { useState, useEffect, useContext } from 'react';
import { Table, Button, OverlayTrigger, Tooltip, Popover } from 'react-bootstrap';

import AppContext from '../AppContext';

import useRequest from '../helper/useRequest';
import useConfirmation from '../helper/Confirmation';
import { toast } from 'react-toastify';

import { customFilterFunction } from '../helper/CustomFilter';
import TableHeader from '../helper/TableHeader';

import Loader from '../helper/Loader';

import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    getFilteredRowModel,
    useReactTable,
} from '@tanstack/react-table'

const columnHelper = createColumnHelper();

function Users() {
    const context = useConfirmation();

    const { request } = useRequest();
    const { getSalutation } = useContext(AppContext);

    const [sorting, setSorting] = useState([]);
    const [columnFilters, setColumnFilters] = useState([]);

    const [users, setUsers] = useState(false);
    const [realUsers, setRealUsers] = useState(false);

    const deleteUser = async (id, recover = false) => {
        let result = await request("/Operator/DeleteUser", { UserID: id, recover });
        let json = result.data;
        if (json.r != 1 && json.r != 2) {
            if (json.message) {
                toast.error(json.message);
            }
            else {
                toast.error("Es ist ein Fehler aufgetreten.");
            }
        }
        else {
            setRealUsers(items => {
                return items.map(item => {
                    if (item.UserID == id) {
                        if (recover) {
                            return { ...item, Status: json.status };
                        } else {
                            return { ...item, Status: "deleted" };
                        }
                    }

                    return item;
                })
            });
            if (recover) {
                toast.success("Der Benutzer wurde erfolgreich wiederhergestellt.");
            } else {
                if (json.r == 2) {
                    toast.info("Da der letzte Nutzer dieser Organisation gelöscht wurde, wurden alle Termine dieser Organisation gelöscht und storniert.");
                } else {
                    toast.success("Der Benutzer wurde erfolgreich gelöscht.");
                }
            }
        }
    }

    const disableUser = async (userId) => {
        let result = await request("/Operator/DisableUser", { userId });
        let json = result.data;
        if (json.r != 1) {
            if (json.message) {
                toast.error(json.message);
            }
            else {
                toast.error("Es ist ein Fehler aufgetreten.");
            }
        }
        else {
            setRealUsers(items => {
                return items.map(item => {
                    if (item.UserID == userId) {
                        return { ...item, Status: "inactive" };
                    }

                    return item;
                })
            });

            toast.success("Der Benutzer wurde erfolgreich deaktiviert.");
        }
    }

    const disableAutomailing = async (userId) => {
        let result = await request("/Operator/DisableAutomailing", { userId });
        let json = result.data;
        if (json.r != 1) {
            toast.error(json.message);
        }
        else {
            setRealUsers(items => {
                return items.map(item => {
                    if (item.UserID == userId) {
                        return { ...item, NewsletterOptIn: false };
                    }

                    return item;
                })
            });

            toast.success("Das Automailing wurde erfolgreich deaktiviert.");
        }
    }

    const enableUser = async (userId) => {
        let result = await request("/Operator/EnableUser", { userId });
        let json = result.data;
        if (json.r != 1) {
            if (json.message) {
                toast.error(json.message);
            }
            else {
                toast.error("Es ist ein Fehler aufgetreten.");
            }
        }
        else {
            setRealUsers(items => {
                return items.map(item => {
                    if (item.UserID == userId) {
                        return { ...item, Status: "approved" };
                    }

                    return item;
                })
            });

            toast.success("Der Benutzer wurde erfolgreich aktiviert.");
        }
    }

    useEffect(() => {
        const fetchData = async () => {
            try {
                let result = await request("/Operator/GetUsers");
                let json = result.data;
                if (json.r != 1) {
                    if (json.message) {
                        toast.error(json.message);
                    }
                    else {
                        toast.error("Es ist ein Fehler aufgetreten.");
                    }
                }
                else {
                    setRealUsers(json.users);
                }
            }
            catch (ex) {
                console.error(ex);
                toast.error("Es ist ein Fehler aufgetreten.");
            }
        };
        fetchData();
    }, []);

    const columns = [
        columnHelper.accessor('Status', {
            header: () => (
                <OverlayTrigger popperConfig={{
                    modifiers: {
                        name: "offset",
                        options: {
                            offset: [0, 20]
                        }
                    }
                }} placement="left" overlay={(
                    <Popover id="business-status-info">
                        <Popover.Header as="h3">Status</Popover.Header>
                        <Popover.Body>
                            <div><span className="bookedCircle me-2"></span>in review</div>
                            <div><span className="bookedCircle booked me-2"></span>approved</div>
                            <div><span className="bookedCircle canceled me-2"></span>rejected</div>
                            <div><span className="bookedCircle deleted me-2"></span>deleted</div>
                        </Popover.Body>
                    </Popover>
                )}>
                    <span>Status</span>
                </OverlayTrigger>
            ),
            cell: info => {
                let statusCircle = <span className="bookedCircle"></span>;
                if (info.getValue() == "approved") {
                    statusCircle = <span className="bookedCircle booked"></span>;
                }
                else if (info.getValue() == "rejected") {
                    statusCircle = <span className="bookedCircle canceled"></span>;
                }
                else if (info.getValue() == "deleted"){
                    statusCircle = <span className="bookedCircle deleted"></span>;
                }
                
                return (
                    <OverlayTrigger placement="left" overlay={<Tooltip>{info.getValue()}</Tooltip>}>
                        <>{statusCircle} {info.getValue()}</>
                    </OverlayTrigger>
                );
            },
            enableColumnFilter: true,
            filterFn: customFilterFunction,
            meta: {
                filterComponent: "select",
            }
        }),
        columnHelper.accessor('SalutationID', {
            header: () => <span>Anrede</span>,
            cell: info => info.getValue(),
            enableColumnFilter: true,
        }),
        columnHelper.accessor('Title', {
            header: () => <span>Titel</span>,
            cell: info => info.getValue(),
            enableColumnFilter: true,
            sortingFn: 'alphanumeric',
        }),
        columnHelper.accessor('FirstName', {
            header: () => <span>Vorname</span>,
            cell: info => info.getValue(),
            enableColumnFilter: true,
            sortingFn: 'alphanumeric',
        }),
        columnHelper.accessor('LastName', {
            header: () => <span>Nachname</span>,
            cell: info => info.getValue(),
            enableColumnFilter: true,
            sortingFn: 'alphanumeric',
        }),
        columnHelper.accessor('Username', {
            header: () => <span>Benutzername</span>,
            cell: info => info.getValue(),
            enableColumnFilter: true,
            sortingFn: 'alphanumeric',
        }),
        columnHelper.accessor('Email', {
            header: () => <span>E-Mail</span>,
            cell: info => info.getValue(),
            enableColumnFilter: true,
            sortingFn: 'alphanumeric',
        }),
        columnHelper.accessor('PhoneNo', {
            header: () => <span>Telefon</span>,
            cell: info => info.getValue(),
            enableColumnFilter: true,
            sortingFn: 'alphanumeric',
        }),
        columnHelper.accessor('OrganisationName', {
            header: () => <span>Organisation</span>,
            cell: info => {
                if (info.row.original.Role === "school") {
                    return <a href={"/Operator/School/" + info.row.original.OrganisationID}>{info.row.original.OrganisationName}</a>
                }
                else if (info.row.original.Role === "business") {
                    return <a href={"/Operator/Business/" + info.row.original.OrganisationID}>{info.row.original.OrganisationName}</a>
                }
            },
            enableColumnFilter: true,
            sortingFn: 'alphanumeric',
        }),
        columnHelper.accessor('OrganisationID', {
            header: () => <span></span>,
            cell: info => {
                return <div className="text-end">
                    {info.row.original.NewsletterOptIn && (
                        <OverlayTrigger overlay={<Tooltip>Automailing deaktivieren</Tooltip>}>
                            <Button size="sm" variant="info" className="me-1" onClick={() => context.openModal("Automailing deaktivieren?", "Möchten Sie das Automailing für den Benutzer wirklich deaktivieren?", () => { disableAutomailing(info.row.original.UserID) }, "Ja", "Nein")}><i className="fa-solid fa-fw fa-mail-bulk"></i></Button>
                        </OverlayTrigger>
                    )}
                    {info.row.original.Status == "approved" && (
                        <OverlayTrigger overlay={<Tooltip>Benutzer deaktivieren</Tooltip>}>
                            <Button size="sm" variant="warning" className="me-1" onClick={() => context.openModal("Wirklich deaktivieren?", "Möchten sie diesen Benutzer wirklich deaktivieren?", () => { disableUser(info.row.original.UserID) }, "Ja", "Nein")}><i className="fa-solid fa-fw fa-lock"></i></Button>
                        </OverlayTrigger>
                    )}
                    {info.row.original.Status == "inactive" && (
                        <OverlayTrigger overlay={<Tooltip>Benutzer aktivieren</Tooltip>}>
                            <Button size="sm" variant="warning" className="me-1" onClick={() => context.openModal("Wirklich aktivieren?", "Möchten sie diesen Benutzer wirklich aktivieren?", () => { enableUser(info.row.original.UserID) }, "Ja", "Nein")}><i className="fa-solid fa-fw fa-lock-open"></i></Button>
                        </OverlayTrigger>
                    )}
                    <OverlayTrigger overlay={<Tooltip>Benutzer löschen</Tooltip>}>
                        <>
                            {info.row.original.Status == "deleted" && <Button size="sm" variant="success" onClick={() => context.openModal("Wirklich wiederherstellen?", "Möchten Sie diesen Benutzer wirklich wiederherstellen?", () => { deleteUser(info.row.original.UserID, true) }, "Ja", "Nein")}><i className="fa-solid fa-fw fa-trash-restore"></i></Button>}
                            {info.row.original.Status != "deleted" && <Button size="sm" variant="danger" onClick={() => context.openModal("Wirklich löschen?", "Möchten Sie diesen Benutzer wirklich löschen?", () => { deleteUser(info.row.original.UserID) }, "Ja", "Nein")}><i className="fa-solid fa-fw fa-trash"></i></Button>}
                        </>
                    </OverlayTrigger>  
                </div>;
            },
            enableSorting: false,
            enableColumnFilter: false,
            size: 140,
        }),
    ]

    const table = useReactTable({
        data: users || [],
        columns,
        state: {
            sorting,
            columnFilters,
        },
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,

        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    });

    useEffect(() => {
        if (!realUsers) return;

        setUsers(realUsers.map(item => {
            const newItem = { ...item };

            newItem.StatusString = newItem.Status;
            newItem.SalutationID = getSalutation(newItem.SalutationID);

            return newItem;
        }));
    }, [realUsers])

    if (users === false) return <Loader />
    return (
        <Table striped>
            <thead>
                {table.getHeaderGroups().map(headerGroup => (
                    <TableHeader key={headerGroup.id} table={table} headerGroup={headerGroup} flexRender={flexRender} />
                ))}
            </thead>
            <tbody>
                {table.getRowModel().rows.map(row => (
                    <tr key={row.id}>
                        {row.getVisibleCells().map(cell => {
                            return (
                                <td key={cell.id}>
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </td>
                            )
                        })}
                    </tr>
                ))}
            </tbody>
        </Table>
    );
}

export default Users;